import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Logo,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@osteopro/ui';
import {
  CalendarDays,
  CalendarPlus,
  ClipboardList,
  FilePlus,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Receipt,
  ReceiptText,
  Settings,
  UserPlus,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { dunningTodoCount, useDunningSummary } from '../../hooks/api/use-dunning.js';
import { useOrganization, usePlatformOnly } from '../../lib/auth-provider.js';
import { OnboardingNavItem } from '../onboarding/OnboardingNavItem.js';
import { PlatformNavGroup } from './PlatformNavGroup.js';
import { RetrocessionNavItem } from './RetrocessionNavItem.js';
import { SidebarCabinetFooter } from './SidebarCabinetFooter.js';

/**
 * Barre latérale, reprise de la V1 à l'identique en apparence. Seules les entrées dont le module
 * existe sont rendues : on ne montre pas un lien vers un écran qui n'est pas encore construit.
 */
export function AppSidebar() {
  const { t } = useTranslation();
  const [location, navigate] = useLocation();
  const { isMobile, setOpenMobile, state, toggleSidebar } = useSidebar();
  // Compte de plateforme sans cabinet : la console seule, rien de ce qui suppose un cabinet.
  const platformOnly = usePlatformOnly();
  const { isOwner } = useOrganization();
  // Même compte que l'onglet Relances : ce qui attend un geste, rafraîchi toutes les 60 s.
  const dunningSummary = useDunningSummary({ enabled: !platformOnly });
  const dunningTodo =
    dunningSummary.data === undefined ? 0 : dunningTodoCount(dunningSummary.data, isOwner);

  const isCollapsed = !isMobile && state === 'collapsed';
  const toggleLabel = isCollapsed ? t('shell:sidebar.expand') : t('shell:sidebar.collapse');

  const go = (url: string) => {
    navigate(url);
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="flex h-16 flex-row items-center gap-2 border-b px-2 py-0">
        <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden px-2 group-data-[collapsible=icon]:hidden">
          <Logo className="h-9 w-9 flex-shrink-0" />
          <div className="flex-1 overflow-hidden">
            <h2 className="truncate text-base font-semibold">{t('common:app.name')}</h2>
            <p className="truncate text-xs text-muted-foreground">{t('common:app.tagline')}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="h-8 w-8 flex-shrink-0"
          aria-label={toggleLabel}
          title={toggleLabel}
          data-testid="button-sidebar-toggle-header"
        >
          {isCollapsed ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </Button>
      </SidebarHeader>

      <SidebarContent className="px-2 py-3">
        {!platformOnly && (
          <>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu className="gap-1">
                  <SidebarMenuItem>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          className="w-full justify-start gap-2 group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0"
                          title={t('common:actions.new')}
                          data-testid="button-sidebar-new"
                        >
                          <Plus className="h-4 w-4 flex-shrink-0" />
                          <span className="font-medium group-data-[collapsible=icon]:hidden">
                            {t('common:actions.new')}
                          </span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side="right" align="start" className="w-56">
                        <DropdownMenuLabel>{t('common:actions.new')}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            go('/agenda?new=1');
                          }}
                          data-testid="link-new-appointment"
                        >
                          <CalendarPlus className="mr-2 h-4 w-4" />
                          {t('agenda:toolbar.new_appointment')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            go('/patients?new=1');
                          }}
                          data-testid="link-new-patient"
                        >
                          <UserPlus className="mr-2 h-4 w-4" />
                          {t('patients:actions.new_patient')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            go('/consultations/new');
                          }}
                          data-testid="link-new-consultation"
                        >
                          <FilePlus className="mr-2 h-4 w-4" />
                          {t('consultations:list.new')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            go('/invoices?tab=invoices&new=1');
                          }}
                          data-testid="link-new-invoice"
                        >
                          <ReceiptText className="mr-2 h-4 w-4" />
                          {t('billing:invoice.new')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <OnboardingNavItem onNavigate={go} />

            <SidebarGroup>
              <SidebarGroupLabel data-testid="label-sidebar-workspace">
                {t('shell:sidebar.workspace')}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-1">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.startsWith('/dashboard')}
                      onClick={() => {
                        go('/dashboard');
                      }}
                      tooltip={t('dashboard:title')}
                      data-testid="nav-dashboard"
                    >
                      <LayoutDashboard className="h-5 w-5" />
                      <span className="font-medium">{t('dashboard:title')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    {/* `nav-calendar` : identifiant de test de la V1, conservé bien que la route
                    canonique soit désormais `/agenda`. */}
                    <SidebarMenuButton
                      isActive={location.startsWith('/agenda')}
                      onClick={() => {
                        go('/agenda');
                      }}
                      tooltip={t('agenda:title')}
                      data-testid="nav-calendar"
                    >
                      <CalendarDays className="h-5 w-5" />
                      <span className="font-medium">{t('agenda:title')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.startsWith('/patients')}
                      onClick={() => {
                        go('/patients');
                      }}
                      tooltip={t('patients:list.title')}
                      data-testid="nav-patients"
                    >
                      <Users className="h-5 w-5" />
                      <span className="font-medium">{t('patients:list.title')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.startsWith('/invoices')}
                      onClick={() => {
                        go('/invoices');
                      }}
                      tooltip={t('billing:title')}
                      data-testid="nav-invoices"
                    >
                      <Receipt className="h-5 w-5" />
                      <span className="font-medium">{t('billing:title')}</span>
                      {dunningTodo > 0 && (
                        <Badge
                          variant="destructive"
                          className="ml-auto h-4 py-0 text-[10px]"
                          data-testid="reminder-count-badge"
                        >
                          <span aria-hidden="true">{dunningTodo}</span>
                          <span className="sr-only">
                            {t('billing:tabs.reminders_todo', { count: dunningTodo })}
                          </span>
                        </Badge>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <RetrocessionNavItem location={location} onNavigate={go} />
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <div className="mx-2 hidden border-t group-data-[collapsible=icon]:block" aria-hidden />

            <SidebarGroup>
              <SidebarGroupLabel data-testid="label-sidebar-configuration">
                {t('shell:sidebar.configuration')}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-1">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.startsWith('/consultation-reasons')}
                      onClick={() => {
                        go('/consultation-reasons');
                      }}
                      tooltip={t('agenda:reasons.title')}
                      data-testid="nav-consultation-reasons"
                    >
                      <ListChecks className="h-5 w-5" />
                      <span className="font-medium">{t('agenda:reasons.nav')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.startsWith('/questionnaires')}
                      onClick={() => {
                        go('/questionnaires');
                      }}
                      tooltip={t('settings:questionnaires.title')}
                      data-testid="nav-questionnaires"
                    >
                      <ClipboardList className="h-5 w-5" />
                      <span className="font-medium">{t('settings:questionnaires.title')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    {/* L'aide est ouverte à **tout membre** : un assistant bloqué doit pouvoir
                    écrire, et c'est la porte que l'écran de cabinet suspendu désigne. */}
                    <SidebarMenuButton
                      isActive={location.startsWith('/aide')}
                      onClick={() => {
                        go('/aide');
                      }}
                      tooltip={t('support:list.title')}
                      data-testid="nav-support"
                    >
                      <LifeBuoy className="h-5 w-5" />
                      <span className="font-medium">{t('support:nav')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location.startsWith('/settings')}
                      onClick={() => {
                        go('/settings');
                      }}
                      tooltip={t('settings:title')}
                      data-testid="nav-settings"
                    >
                      <Settings className="h-5 w-5" />
                      <span className="font-medium">{t('settings:title')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {/* Exploitation de la plateforme : rendu seulement pour qui en porte le rôle. */}
        <PlatformNavGroup onNavigate={go} />
      </SidebarContent>

      {!platformOnly && <SidebarCabinetFooter />}
    </Sidebar>
  );
}
